import {
  TwitterCrisisMonitorMainDetractorsDTO,
  TwitterCrisisMonitorMainDetractorsItemDTO,
} from '@/data/dto/twitter-crisis-monitor/main-detractors.dto';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
  PaginatedRequestParamsDTO,
  ResponseMetaTypeDTO,
} from '@/data/dto/common.dto';
import store from '@/store';
import MOMABffService from '@/services/moma-bff.service';
import { FilterStoreContract } from '@/data/store/filter.store.contract';
import { TwitterCrisisMonitorAttackDateFiltersDTO } from '@/data/dto/twitter-crisis-monitor/date-filters.dto';
import { MainDetractorsStoreContract } from '@/data/store/main-detractors.store.contract';

type BasicState<T> = {
  loading: boolean;
  data: T | null;
  meta?: ResponseMetaTypeDTO;
  error: string | null;
  pagination?: PaginatedRequestParamsDTO;
};

type LocalState = BasicState<TwitterCrisisMonitorMainDetractorsDTO>;

@Module({
  dynamic: true,
  namespaced: true,
  name: 'RealtimeMainDetractorsStore',
  store,
})
export class RealtimeMainDetractorsStore
  extends VuexModule
  implements FilterStoreContract, MainDetractorsStoreContract
{
  bffService = new MOMABffService();

  paginationLimit = 10;

  _state: LocalState = {
    loading: false,
    error: null,
    data: null,
    pagination: {
      offset: 0,
      limit: 10,
    },
  };

  get isLoading(): boolean {
    return this._state.loading;
  }

  get all(): TwitterCrisisMonitorMainDetractorsItemDTO[] {
    if (!this._state.data) {
      return [];
    }

    return this._state.data.mainDetractors;
  }

  get maxAttacksCount(): number {
    return this.all[0].attacksCount;
  }

  get allForSelect() {
    return this.all.map((item) => item.user);
  }

  get paginated(): TwitterCrisisMonitorMainDetractorsItemDTO[] {
    const start = this._state.pagination?.offset || 0;
    const end = start + (this._state.pagination?.limit || this.paginationLimit);

    return this.all.slice(start, end);
  }

  get totalCount(): number {
    return this._state.meta?.totalCount || 0;
  }

  @Mutation
  updateMainDetractorsState(payload: Partial<LocalState>): void {
    this._state = {
      ...this._state,
      ...payload,
    };
  }

  @Action
  async fetch(payload: {
    pagination?: PaginatedRequestParamsDTO;
    filters?: TwitterCrisisMonitorAttackDateFiltersDTO;
  }): Promise<void> {
    const { pagination } = payload;

    const validatedPagination = pagination || {
      limit: this.paginationLimit,
      offset: 0,
    };

    if (this._state.data) {
      this.updateMainDetractorsState({
        pagination: validatedPagination,
      });
      return;
    }

    await this._getMainDetractors();
  }

  @Action
  async _getMainDetractors(): Promise<void> {
    this.updateMainDetractorsState({
      loading: true,
      error: null,
      meta: undefined,
      pagination: {
        offset: 0,
        limit: 10,
      },
    });

    try {
      const response =
        await this.bffService.getTwitterCrisisMonitorRealtimeMainDetractors();
      this.updateMainDetractorsState({
        data: response.data,
        meta: response.meta,
      });
    } catch (error: any) {
      this.updateMainDetractorsState({
        error: error?.message,
      });
    } finally {
      this.updateMainDetractorsState({
        loading: false,
      });
    }
  }
}
